import { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api, fmtDate, CATEGORY_LABELS } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, GitBranch, ArrowRight } from "lucide-react";
import { toast } from "sonner";

export default function Traceability() {
  const [params, setParams] = useSearchParams();
  const nav = useNavigate();
  const [q, setQ] = useState(params.get("batch") || "");
  const [mode, setMode] = useState("backward"); // material->production
  const [batchData, setBatchData] = useState(null);
  const [prodData, setProdData] = useState(null);

  const search = useCallback(async (term) => {
    const val = (term ?? q).trim();
    if (!val) return;
    setBatchData(null); setProdData(null);
    if (/^PB-/i.test(val)) {
      try { const { data } = await api.get(`/traceability/production/${val}`); setProdData(data); }
      catch { toast.error("Batch produksi tidak ditemukan."); }
    } else {
      try { const { data } = await api.get(`/traceability/batch/${val}`); setBatchData(data); }
      catch { toast.error("Batch bahan tidak ditemukan."); }
    }
  }, [q]);

  useEffect(() => { const b = params.get("batch"); if (b) { setQ(b); search(b); } }, []);

  return (
    <div className="space-y-5">
      <div><h1 className="font-heading text-2xl font-extrabold flex items-center gap-2"><GitBranch className="w-6 h-6" /> Traceability Dua Arah</h1>
        <p className="text-sm text-muted-foreground">Telusuri batch bahan (RM/PK-...) ke produk jadi, atau batch produksi (PB-...) ke bahan baku.</p></div>

      <Card className="p-4">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input data-testid="trace-input" placeholder="Masukkan kode batch (mis. RM-261009-0001 atau PB-261009-001)" value={q}
              onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()} className="pl-9" />
          </div>
          <Button data-testid="trace-search" onClick={() => search()}>Telusuri</Button>
        </div>
      </Card>

      {batchData && (
        <div className="space-y-4">
          <Card className="p-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div><span className="batch-chip text-sm">{batchData.receipt.internal_batch}</span>
                <h3 className="font-heading font-bold mt-1">{batchData.receipt.material_name}</h3>
                <p className="text-sm text-muted-foreground">{CATEGORY_LABELS[batchData.receipt.category]} • {batchData.receipt.supplier_name}</p></div>
              <StatusBadge status={batchData.receipt.status} />
            </div>
          </Card>
          <div className="flex items-center gap-3 flex-wrap text-sm font-medium">
            <div className="px-3 py-2 rounded-lg bg-blue-50 border border-blue-200">Pemasok: {batchData.receipt.supplier_name}</div>
            <ArrowRight className="w-4 h-4" />
            <div className="px-3 py-2 rounded-lg bg-indigo-50 border border-indigo-200">Batch: {batchData.receipt.internal_batch}</div>
            <ArrowRight className="w-4 h-4" />
            <div className="px-3 py-2 rounded-lg bg-emerald-50 border border-emerald-200">Produksi ({batchData.production_batches.length})</div>
          </div>
          <Card className="p-4">
            <h4 className="font-heading font-bold text-sm mb-2">Produk Jadi yang Memakai Bahan Ini</h4>
            {batchData.production_batches.length === 0 ? <div className="text-sm text-muted-foreground">Belum digunakan dalam batch produksi mana pun.</div> :
              batchData.production_batches.map((p) => (
                <div key={p.id} className="flex justify-between py-2 border-b border-slate-100 cursor-pointer hover:bg-accent/50" onClick={() => nav(`/production/${p.id}`)}>
                  <span className="batch-chip">{p.code}</span><span className="text-sm">{p.product_name}</span>
                </div>
              ))}
          </Card>
          <Card className="p-4">
            <div className="flex justify-between"><h4 className="font-heading font-bold text-sm mb-2">Detail Batch</h4>
              <Button size="sm" variant="outline" onClick={() => nav(`/receipts/${batchData.receipt.id}`)}>Buka Detail Penuh</Button></div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
              <Info k="Diterima" v={`${batchData.receipt.jumlah} ${batchData.receipt.unit}`} />
              <Info k="Terpakai" v={batchData.qty_used} />
              <Info k="Sisa" v={batchData.qty_remaining} />
              <Info k="ED" v={fmtDate(batchData.receipt.expiry_date)} />
            </div>
          </Card>
        </div>
      )}

      {prodData && (
        <div className="space-y-4">
          <Card className="p-4">
            <span className="batch-chip text-sm">{prodData.production.code}</span>
            <h3 className="font-heading font-bold mt-1">{prodData.production.product_name}</h3>
            <p className="text-sm text-muted-foreground">{prodData.production.qty} {prodData.production.unit} • {fmtDate(prodData.production.production_date)}</p>
          </Card>
          <Card className="p-4">
            <h4 className="font-heading font-bold text-sm mb-2">Bahan Baku yang Digunakan</h4>
            {prodData.materials.length === 0 ? <div className="text-sm text-muted-foreground">Belum ada bahan dikeluarkan untuk batch ini.</div> :
              <table className="w-full text-sm"><thead><tr className="text-left text-muted-foreground border-b">
                <th className="py-2 font-medium">Batch Bahan</th><th className="font-medium">Bahan</th><th className="font-medium">Pemasok</th><th className="font-medium">Jumlah</th></tr></thead>
                <tbody>{prodData.materials.map((m) => (
                  <tr key={m.internal_batch} className="border-b border-slate-100 cursor-pointer hover:bg-accent/50" onClick={() => { setQ(m.internal_batch); search(m.internal_batch); }}>
                    <td className="py-2"><span className="batch-chip">{m.internal_batch}</span></td><td>{m.material_name}</td><td>{m.supplier_name}</td><td>{m.qty} {m.unit}</td>
                  </tr>))}</tbody>
              </table>}
          </Card>
        </div>
      )}
    </div>
  );
}
const Info = ({ k, v }) => <div><div className="text-xs text-muted-foreground">{k}</div><div className="font-medium">{v}</div></div>;
